import { DateTime } from "luxon";
import { useMemo } from "react";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useLastNameFirst } from "../../../hooks/useNameFormat";
import { useRosterLookup } from "../../../hooks/useRosterLookup";
import { useScheduleDays } from "../../../hooks/useScheduleDays";
import { useScheduleCacheEnabled } from "../../../hooks/useScheduleWeekRules";
import { useScheduleWeeks } from "../../../hooks/useScheduleWeeks";
import { useShownWeek } from "../../../hooks/useShownWeek";
import { getWeekStart } from "../../../utils/dateUtils";
import { DEFAULT_WEEK_START_DAY, type Nurse, type ScheduleData } from "../../../utils/models";
import { panelWeeksOf } from "../../../utils/panelWeeks";
import { weekStartsBetween } from "../../../utils/scheduleWeeks";
import { type ExportableWeek, exportableWeeks, weekIndicesInRange } from "./weekRange";

const DAYS_PER_WEEK = 7;
const NO_NURSES: Nurse[] = [];

type ExportRange = { start: DateTime; end: DateTime };

export type ExportWeeks = {
	/** The weeks the pickers offer, in order. */
	weeks: ExportableWeek[];
	/** The picked range, else the shown week. */
	rangeStart: DateTime | undefined;
	rangeEnd: DateTime | undefined;
	/** What the PDF draws, and `weekIndices` are positions in. */
	schedule: ScheduleData;
	weekIndices: number[];
	isLoading: boolean;
	isError: boolean;
};

/**
 * The weekly export's weeks. With `staffing_schedule_cache` off, the old list's loaded weeks, as before. On, every Sunday
 * week of the schedule is offered, and only the picked range's weeks are read from the cache to draw.
 */
export const useExportWeeks = (shownWeekIndex: number, range: ExportRange | null): ExportWeeks => {
	const isCacheEnabled = useScheduleCacheEnabled();
	const { hospitalData, chosenDepartment } = useHospitalData();
	const { weekStart } = useShownWeek();
	const lastNameFirst = useLastNameFirst();
	const roster = useRosterLookup(chosenDepartment.department_id);
	// DEFAULT: today's week until the page names one.
	const shownWeekStart = getWeekStart(weekStart ?? DateTime.now(), DEFAULT_WEEK_START_DAY);
	const { firstDate, lastDate } = useScheduleWeeks(shownWeekStart);

	const loadedWeeks = useMemo(() => exportableWeeks(hospitalData), [hospitalData]);
	const scheduleWeeks = useMemo<ExportableWeek[]>(
		() =>
			firstDate && lastDate
				? weekStartsBetween(firstDate, lastDate, DEFAULT_WEEK_START_DAY).map((start, weekIndex) => ({
						weekIndex,
						start: DateTime.fromISO(start, { zone: chosenDepartment.timezone }),
					}))
				: [],
		[firstDate, lastDate, chosenDepartment.timezone],
	);
	const weeks = isCacheEnabled ? scheduleWeeks : loadedWeeks;
	// The viewed week, or the earliest exportable one when that week carries no shift rows to date it.
	const defaultStart = isCacheEnabled ? shownWeekStart : (loadedWeeks.find((week) => week.weekIndex === shownWeekIndex) ?? loadedWeeks.at(0))?.start;
	const rangeStart = range?.start ?? defaultStart;
	const rangeEnd = range?.end ?? defaultStart?.plus({ days: DAYS_PER_WEEK - 1 });

	// DEFAULT: with no range yet, the shown week's days, which the page already holds.
	const rangeDays = useScheduleDays(rangeStart ?? shownWeekStart, rangeEnd ?? shownWeekStart.plus({ days: DAYS_PER_WEEK - 1 }));
	const rangeSchedule = useMemo<ScheduleData>(
		// DEFAULT: no one until the roster arrives; the export waits for it (`isLoading`).
		() => ({ weeks: panelWeeksOf(rangeDays.days, roster.getNurseById, lastNameFirst), nurses: roster.roster ?? NO_NURSES }),
		[rangeDays.days, roster.getNurseById, roster.roster, lastNameFirst],
	);

	if (!isCacheEnabled) {
		return {
			weeks,
			rangeStart,
			rangeEnd,
			schedule: hospitalData,
			weekIndices: weekIndicesInRange(weeks, rangeStart, rangeEnd),
			isLoading: false,
			isError: false,
		};
	}
	return {
		weeks,
		rangeStart,
		rangeEnd,
		schedule: rangeSchedule,
		weekIndices: exportableWeeks(rangeSchedule).map((week) => week.weekIndex),
		isLoading: rangeDays.isLoading || roster.isLoading,
		isError: rangeDays.isError || roster.isError,
	};
};
